import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  ChevronRight, ArrowLeft, Lock, CheckCircle2, Video, Briefcase, 
  Star, Trophy, Flame, BookOpen, Play
} from 'lucide-react';
import axios from 'axios';
import { getCabinModulesArray as getStaticModules } from '../data/cabinCrewCurriculum';
import { fetchAllLevels } from '../services/curriculumService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CabinTrainingHome() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);

  useEffect(() => {
    loadModules();
    fetchProgress();
  }, []);

  const loadModules = async () => {
    try {
      // Önce API'den dene (cabin_crew type)
      const apiModules = await fetchAllLevels('cabin_crew');
      if (apiModules && apiModules.length > 0) {
        setModules(apiModules);
        return;
      }
    } catch (error) {
      console.log('API error, falling back to static');
    }
    // Fallback: Static dosyadan yükle
    setModules(getStaticModules());
  };

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/cabin-training/progress`, { withCredentials: true });
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching cabin progress:', error);
      // Default progress if API not ready
      setProgress({
        completed_modules: [],
        completed_units: [],
        current_module: 1,
        total_score: 0,
        xp: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Admin tarafından açılan modüller
  const unlockedModules = user?.cabin_progress?.unlocked_modules || [];
  const allUnlocked = user?.kariyer_progress?.all_unlocked || false;
  
  const isModuleUnlocked = (moduleIndex, moduleId) => {
    // Admin için tüm modüller açık
    if (user?.role === 'admin') return true;
    
    // Admin tüm eğitimleri açtıysa
    if (allUnlocked) return true;
    
    // Modül numarasını al (1-16)
    const moduleNum = moduleId ? parseInt(moduleId.replace('module_', '')) : moduleIndex + 1;
    
    // Admin bu modülü özellikle açtıysa
    if (unlockedModules.includes(moduleNum)) return true;
    
    // Modül 11, 12, 13, 14, 15, 16 herkese açık
    if (moduleNum >= 11 && moduleNum <= 16) return true;
    
    // Modül 1 ve 2 yeni başlayanlara açık
    if (moduleNum === 1 || moduleNum === 2) return true;
    
    // Modül 3-10 için aşama aşama kilitleme
    if (!progress) return false;
    
    const prevModuleId = `module_${moduleNum - 1}`;
    return progress.completed_modules?.includes(prevModuleId) || moduleNum <= (progress.current_module || 2);
  };

  const isModuleCompleted = (moduleId) => {
    return progress?.completed_modules?.includes(moduleId);
  };

  const getModuleProgress = (module) => {
    if (!progress) return 0;
    const completedUnits = progress.completed_units?.filter(u => u.startsWith(`CM-M${module.module_id}`)) || [];
    const totalUnits = module.units?.length || 1;
    return Math.round((completedUnits.length / totalUnits) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-500 to-teal-600 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-bounce text-6xl mb-4">✈️</div>
          <p className="text-white font-medium">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-4 pt-6 pb-8 rounded-b-[2rem]">
        <div className="max-w-4xl mx-auto">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Ana Menü</span>
            </button>
            
            {/* Stats */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur px-3 py-1.5 rounded-full">
                <Trophy className="w-4 h-4 text-yellow-300" />
                <span className="text-white font-bold text-sm">{progress?.total_score || 0}</span>
              </div>
              <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur px-3 py-1.5 rounded-full">
                <Star className="w-4 h-4 text-yellow-300" />
                <span className="text-white font-bold text-sm">{progress?.xp || 0} Mil</span>
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="text-center text-white">
            <h1 className="text-2xl font-bold mb-1">Kabin Memurluğu Eğitimi</h1>
            <p className="text-emerald-100 text-sm">Mülakat Hazırlık & Mesleki Eğitim</p>
          </div>

          {/* Overall Progress Card */}
          <div className="mt-6 bg-white rounded-2xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-gray-500 text-sm">Genel İlerleme</p>
                <p className="text-xl font-bold text-gray-800">
                  {(progress?.completed_modules?.length || 0) + (progress?.completed_lessons?.length || 0) > 0 
                    ? `${(progress?.completed_modules?.length || 0)} Modül, ${progress?.completed_lessons?.length || 0} Ders`
                    : `${progress?.completed_modules?.length || 0} / ${8} Modül`
                  }
                </p>
                {(progress?.xp > 0 || progress?.miles > 0) && (
                  <p className="text-sm text-emerald-600 font-medium mt-1">
                    ⭐ {progress?.xp || progress?.miles || 0} Mil Kazanıldı
                  </p>
                )}
              </div>
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <span className="text-2xl font-bold text-emerald-600">
                  {progress?.completed_lessons?.length > 0 
                    ? `${progress?.completed_lessons?.length}`
                    : `${Math.round(((progress?.completed_modules?.length || 0) / 7) * 100)}%`
                  }
                </span>
              </div>
            </div>
            <Progress 
              value={progress?.completed_lessons?.length > 0 
                ? Math.min(100, (progress?.completed_lessons?.length / 50) * 100)
                : ((progress?.completed_modules?.length || 0) / 7) * 100
              } 
              className="h-2"
            />
          </div>
        </div>
      </div>
      {/* Modules List */}
      <div className="max-w-4xl mx-auto px-4 mt-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Eğitim Modülleri</h2>
        
        <div className="space-y-4">
          {modules.filter(m => [1, 11, 12, 13, 14, 15, 16].includes(m.module_id)).map((module, index) => {
            const unlocked = isModuleUnlocked(index, module.id);
            const completed = isModuleCompleted(module.id);
            const moduleProgress = getModuleProgress(module);
            
            return (
              <Card 
                key={module.id}
                className={`overflow-hidden transition-all duration-300 border-0 shadow-md ${
                  unlocked 
                    ? 'cursor-pointer hover:shadow-lg' 
                    : 'opacity-60 cursor-not-allowed'
                }`}
                onClick={() => unlocked && navigate(`/cabin-training/module/${module.module_id}`)}
              >
                <CardContent className="p-0">
                  <div className="flex items-center">
                    {/* Icon Section */}
                    <div className={`w-20 h-24 bg-gradient-to-br ${module.color} flex items-center justify-center relative`}>
                      <span className="text-3xl">{module.icon}</span>
                      {completed && (
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <CheckCircle2 className="w-4 h-4 text-white" />
                        </div>
                      )}
                      {!unlocked && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <Lock className="w-6 h-6 text-white" />
                        </div>
                      )}
                    </div>
                    
                    {/* Content Section */}
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-gray-400">MODÜL {module.module_id}</span>
                            {completed && (
                              <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                Tamamlandı
                              </span>
                            )}
                          </div>
                          <h3 className="font-bold text-gray-900">Modül {index + 1}: {module.title.replace(/Modül \d+: /, "")}</h3>
                          <p className="text-sm text-gray-500 mt-0.5">{module.subtitle}</p>
                          
                          {/* Progress */}
                          {unlocked && !completed && moduleProgress > 0 && (
                            <div className="mt-2">
                              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                <span>{moduleProgress}% tamamlandı</span>
                              </div>
                              <Progress value={moduleProgress} className="h-1.5" />
                            </div>
                          )}
                          
                          {/* Info */}
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <BookOpen className="w-3 h-3" />
                              {module.totalUnits || module.units?.length} ünite
                            </span>
                            <span className="flex items-center gap-1">
                              <Play className="w-3 h-3" />
                              ~{module.estimatedTime} dk
                            </span>
                          </div>
                        </div>
                        
                        <ChevronRight className={`w-5 h-5 ${unlocked ? 'text-gray-400' : 'text-gray-300'}`} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Final Result Card */}
        {progress?.final_result && (
          <Card className="mt-6 border-2 border-yellow-400 bg-yellow-50">
            <CardContent className="p-6 text-center">
              <Trophy className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Final Sonucunuz</h3>
              <p className={`text-2xl font-bold ${
                progress.final_result === 'ready' ? 'text-green-600' :
                progress.final_result === 'conditional' ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {progress.final_result === 'ready' ? '✅ Mülakata Hazır' :
                 progress.final_result === 'conditional' ? '⚠️ Geliştirilmeli' : '❌ Şu An Uygun Değil'}
              </p>
              <p className="text-gray-500 mt-2">Toplam Puan: {progress.total_score}/100</p>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="mt-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <span className="text-lg">💡</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Nasıl Çalışır?</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Her modülü sırayla tamamlayın. Senaryolarda doğru cevapları seçerek puan kazanın. 
                  Final değerlendirmesinde %70 ve üzeri puan alarak "Mülakata Hazır" durumuna ulaşın.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
